import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, AppRole } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, Settings, Menu, X, Gift, Star, Home, FileText, Coins, Award, Heart, Zap, Trophy, Calendar, Ticket, Users } from 'lucide-react';
import { AuthModal } from './AuthModal';
import { CreditBalance } from './CreditBalance';
import { NotificationBell } from './NotificationBell';
import { MobileMenu } from './MobileMenu';
import { supabase } from '@/integrations/supabase/client';
import { useBranding } from '@/hooks/useBranding';
import { Badge } from '@/components/ui/badge';

// Role display configuration
const getRoleBadgeConfig = (role: AppRole | null, isAdmin: boolean, isModerator: boolean): { label: string; className: string } | null => {
  if (role === 'super_admin') {
    return { label: 'Super Admin', className: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800' };
  }
  if (isAdmin) {
    return { label: 'Admin', className: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800' };
  }
  if (isModerator) {
    return { label: 'Partner Admin', className: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' };
  }
  return null; // Don't show badge for regular users
};

interface MenuItem {
  label: string;
  href: string;
  visible: boolean;
  highlight?: boolean;
  icon?: string;
}

interface MenuSettings {
  items: MenuItem[];
}

const iconMap: Record<string, React.ElementType> = {
  Gift, Star, Home, FileText, Coins, Award, Heart, Zap, Calendar, Ticket,
};

const DEFAULT_MENU_ITEMS: MenuItem[] = [
  { label: 'Matches', href: '/', visible: true, icon: 'Calendar' },
  { label: 'Coupons', href: '/coupons', visible: true, icon: 'Ticket' },
  { label: 'Betting Sites', href: '/best-betting-sites', visible: true, icon: 'Star' },
  { label: 'Free Credits', href: '/rewards', visible: true, highlight: true, icon: 'Gift' },
];

async function fetchMenuItems(): Promise<MenuItem[]> {
  const { data } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', 'site_menu')
    .maybeSingle();
  
  if (data?.value && typeof data.value === 'object' && !Array.isArray(data.value)) {
    const settings = data.value as unknown as MenuSettings;
    if (settings.items?.length > 0) {
      return settings.items;
    }
  }
  return DEFAULT_MENU_ITEMS;
}

export function Header() {
  const { user, isAdmin, isModerator, hasAdminPanelAccess, userRole, signOut } = useAuth();
  const { branding } = useBranding();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch menu items with React Query for stable caching
  const { data: menuItems = DEFAULT_MENU_ITEMS } = useQuery({
    queryKey: ['site-menu'],
    queryFn: fetchMenuItems,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    placeholderData: DEFAULT_MENU_ITEMS,
  });

  // Memoize site name - use stable default to prevent flash
  const siteName = useMemo(() => branding.site_name, [branding.site_name]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const visibleItems = menuItems.filter(item => item.visible);

  const renderIcon = (iconName?: string) => {
    if (!iconName) return null;
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : null;
  };

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header className="sticky top-0 z-50 w-full border-b border-border/80 bg-card/95 backdrop-blur-lg safe-area-inset shadow-sm" role="banner">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 max-w-4xl">
          {/* Logo - 44px touch target, responsive for mobile/desktop */}
          <Link 
            to="/" 
            className="flex items-center gap-2.5 min-h-[44px] min-w-[44px]"
            aria-label="Go to WinWGo homepage"
          >
            {/* Mobile: Show compact icon */}
            <div className="sm:hidden flex items-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary shadow-sm shadow-primary/20">
                <Trophy className="h-5 w-5 text-primary-foreground" aria-hidden="true" />
              </div>
            </div>

            {/* Desktop: Show icon + brand name */}
            <div className="hidden sm:flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary shadow-sm shadow-primary/20">
                <Trophy className="h-5 w-5 text-primary-foreground" aria-hidden="true" />
              </div>
              <span className="text-lg font-bold tracking-tight text-foreground">{siteName}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
            {visibleItems.map((item, index) => (
              <Link 
                key={index}
                to={item.href} 
                className={`text-sm font-medium transition-colors flex items-center gap-2 py-3 min-h-[44px] ${
                  item.highlight 
                    ? 'text-amber-500 hover:text-amber-400' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {renderIcon(item.icon)}
                {item.label}
              </Link>
            ))}
            {/* Partner Admin entry - moderators only */}
            {isModerator && !isAdmin && (
              <Link to="/admin" className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors py-3 min-h-[44px] flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                Partner Admin
              </Link>
            )}
            {/* Full Admin entry */}
            {isAdmin && (
              <Link to="/admin" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-3 min-h-[44px] flex items-center">
                Admin
              </Link>
            )}
          </nav>

          {/* Right Actions - All buttons 44px minimum */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <CreditBalance />
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="rounded-full h-11 w-11 hover:bg-secondary"
                      aria-label="Open user menu"
                    >
                      <User className="h-5 w-5" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    {/* User info with role badge */}
                    <div className="px-2 py-3 border-b border-border/50">
                      <p className="text-sm font-medium text-foreground truncate">{user.email}</p>
                      {(() => {
                        const roleBadge = getRoleBadgeConfig(userRole, isAdmin, isModerator);
                        return roleBadge ? (
                          <Badge variant="outline" className={`mt-1.5 text-xs font-medium ${roleBadge.className}`}>
                            {roleBadge.label}
                          </Badge>
                        ) : null;
                      })()}
                    </div>
                    {/* Partner Admin dropdown entry */}
                    {isModerator && !isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="py-3 min-h-[44px] text-blue-600">
                        <Users className="mr-3 h-5 w-5" />
                        Partner Admin
                      </DropdownMenuItem>
                    )}
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="py-3 min-h-[44px]">
                        <Settings className="mr-3 h-5 w-5" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleSignOut} className="py-3 min-h-[44px] text-destructive focus:text-destructive">
                      <LogOut className="mr-3 h-5 w-5" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button onClick={() => setShowAuthModal(true)} className="h-11 text-sm px-5 font-semibold shadow-sm">
                Sign In
              </Button>
            )}

            {/* Mobile menu toggle - 44px */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-11 w-11"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close main menu" : "Open main menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </Button>
          </div>
        </div>

        {/* Premium Mobile Menu */}
        {mobileMenuOpen && (
          <MobileMenu
            menuItems={menuItems}
            isAdmin={isAdmin}
            isModerator={isModerator && !isAdmin}
            isLoggedIn={!!user}
            onClose={() => setMobileMenuOpen(false)}
            onSignOut={handleSignOut}
          />
        )}
      </header>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </>
  );
}
